import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarClock, LogIn } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  };

  const sendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotBusy(true);
    try {
      const { api } = await import("@/lib/api");
      await api.post("/auth/password-reset/request", { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch { setForgotSent(true); }  // never reveal existence
    setForgotBusy(false);
  };

  return (
    <div className="min-h-screen w-full bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-blue-600 grid place-items-center text-white shadow-sm">
            <CalendarClock size={22} />
          </div>
          <div className="mt-3 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Patient Access
          </div>
          <div
            className="mt-3 text-xs sm:text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center leading-snug"
            data-testid="login-sharepoint-notice"
          >
            Please use <b>SharePoint (old site)</b> for schedules going forward.
            <span className="block text-[11px] text-amber-800 mt-0.5">9/29/26 update</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <form className="space-y-4" onSubmit={submit} data-testid="login-form">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email" data-testid="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" required className="mt-1.5"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required className="mt-1.5"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600" data-testid="login-error">{error}</div>
            )}
            <Button
              type="submit" disabled={busy}
              className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              data-testid="login-submit"
            >
              <LogIn size={16} className="mr-2" />
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setForgotEmail(email); setForgotOpen(true); setForgotSent(false); }}
                className="text-xs text-blue-600 hover:underline"
                data-testid="login-forgot"
              >
                Forgot password?
              </button>
            </div>
          </form>
        </div>

        {forgotOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" data-testid="forgot-dialog">
              <div className="p-5 border-b border-slate-200">
                <div className="font-display font-semibold">Reset your password</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Enter your work email and we&apos;ll send you a reset link. The link is valid for 45 minutes.
                </div>
              </div>
              {forgotSent ? (
                <div className="p-6 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">✓</div>
                  <div className="font-medium">Check your inbox</div>
                  <div className="text-xs text-slate-500">
                    If <b>{forgotEmail}</b> matches an account, a reset link is on its way.
                  </div>
                  <Button className="w-full mt-2" variant="outline" onClick={() => setForgotOpen(false)} data-testid="forgot-close">
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={sendReset} className="p-5 space-y-3">
                  <div>
                    <Label htmlFor="fe">Work email</Label>
                    <Input id="fe" type="email" value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      autoFocus required className="mt-1.5"
                      data-testid="forgot-email"
                      placeholder="you@company.com" />
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" type="button" onClick={() => setForgotOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={forgotBusy}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="forgot-submit">
                      {forgotBusy ? "Sending…" : "Send reset link"}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
